/**
 * contracts/adapter.md §8 — ZIP in and out.
 *
 * ZERO DEPENDENCIES, deliberately. Every JS ZIP library ships its own DEFLATE implementation,
 * which is 20–90 KB on the critical path of every tool page — and the platform has had
 * `CompressionStream`/`DecompressionStream('deflate-raw')` since Safari 16.4, which is the
 * same baseline `Intl.Segmenter` already forces. So the archive FORMAT is implemented here
 * (a few hundred lines of little-endian record parsing) and the COMPRESSION is the
 * platform's. That also removes a licence question from the bundle entirely.
 *
 * READ: one input per entry. Directory entries and anything the parsers do not recognise are
 * skipped WITH A PER-FILE REASON, never fatally — one malformed file in forty must not fail
 * the set.
 *
 * ENTRY NAMES ARE HOSTILE INPUT. See core/adapter/filenames.ts: absolute paths and `..`
 * segments are rejected, control characters and separators stripped, length capped, and
 * Windows-reserved basenames renamed. The name never touches a filesystem here, but it
 * reaches `InputRef.name` and then the OUTPUT ZIP.
 *
 * WRITE: incremental. The memory peak lands during parse, not during write, and
 * materialising every converted document plus the archive at once is what breaks a large
 * batch. `ZipWriter.add` streams each entry out as it arrives.
 *
 * NO FILE CAP, NO SIZE CAP, NO FILENAME WATERMARK. A stated product position.
 */

import { sanitizeZipEntryName, uniqueName } from '../../core/adapter/filenames.ts';
import {
  CENTRAL_HEADER_SIG,
  centralRecord,
  crc32,
  END_OF_CENTRAL_SIG,
  endOfCentral,
  LOCAL_HEADER_SIG,
  localHeader,
  METHOD_DEFLATE,
  METHOD_STORE,
  type ZipRecord,
} from '../../core/adapter/zip-records.ts';

// The record layout and CRC-32 live in CORE (`zip-records.ts`) because the DOCX writer builds a ZIP
// too and may not import this file. Re-exported so this module's API is unchanged.
export { crc32 };

/*───────────────────────────────────────────────────────────────────────────────────────
  Constants and little-endian helpers
───────────────────────────────────────────────────────────────────────────────────────*/

const ZIP64_END_LOCATOR_SIG = 0x07064b50;
const ZIP64_END_SIG = 0x06064b50;
const DATA_DESCRIPTOR_SIG = 0x08074b50;

const u16 = (v: DataView, at: number): number => v.getUint16(at, true);
const u32 = (v: DataView, at: number): number => v.getUint32(at, true);

/*───────────────────────────────────────────────────────────────────────────────────────
  Reading
───────────────────────────────────────────────────────────────────────────────────────*/

/** Why an entry was skipped. Surfaced per file; never fatal for the archive. */
export type SkipReason =
  | 'directory'
  | 'unsafe-name'
  | 'encrypted'
  | 'unsupported-compression'
  | 'corrupt'
  | 'crc-mismatch'
  | 'empty';

export interface ZipEntry {
  /** Sanitised, collision-resolved name. Safe to use as `InputRef.name`. */
  readonly name: string;
  /** The name exactly as the archive carried it, for a diagnostic. */
  readonly rawName: string;
  readonly bytes: Uint8Array;
}

export interface ZipSkip {
  readonly rawName: string;
  readonly reason: SkipReason;
  readonly detail: string;
}

export interface ZipReadResult {
  readonly entries: readonly ZipEntry[];
  readonly skipped: readonly ZipSkip[];
}

export function isZip(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07)
  );
}

interface CentralEntry {
  readonly rawName: string;
  readonly method: number;
  readonly compressedSize: number;
  readonly uncompressedSize: number;
  readonly localOffset: number;
  readonly crc: number;
  readonly flags: number;
  readonly isDirectory: boolean;
}

/**
 * Locates and walks the central directory. Reading the CENTRAL directory rather than
 * scanning local headers is what makes a truncated or slightly corrupt archive recoverable:
 * every entry is described independently, so one bad entry costs one entry.
 */
function readCentralDirectory(bytes: Uint8Array): readonly CentralEntry[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // EOCD is at the end, after a comment of up to 65535 bytes.
  const maxBack = Math.min(bytes.length, 0xffff + 22);
  let eocd = -1;
  for (let i = bytes.length - 22; i >= bytes.length - maxBack; i--) {
    if (i < 0) break;
    if (u32(view, i) === END_OF_CENTRAL_SIG) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new RangeError('not a ZIP archive: no end-of-central-directory record');

  let count = u16(view, eocd + 10);
  let cdOffset = u32(view, eocd + 16);

  // ZIP64: the 32-bit fields saturate and the real values live in the ZIP64 EOCD.
  if (cdOffset === 0xffffffff || count === 0xffff) {
    for (let i = eocd - 20; i >= 0; i--) {
      if (u32(view, i) === ZIP64_END_LOCATOR_SIG) {
        const z64 = Number(view.getBigUint64(i + 8, true));
        if (u32(view, z64) === ZIP64_END_SIG) {
          count = Number(view.getBigUint64(z64 + 32, true));
          cdOffset = Number(view.getBigUint64(z64 + 48, true));
        }
        break;
      }
    }
  }

  const out: CentralEntry[] = [];
  let at = cdOffset;
  for (let i = 0; i < count && at + 46 <= bytes.length; i++) {
    if (u32(view, at) !== CENTRAL_HEADER_SIG) break;
    const flags = u16(view, at + 8);
    const method = u16(view, at + 10);
    const crc = u32(view, at + 16);
    let compressedSize = u32(view, at + 20);
    let uncompressedSize = u32(view, at + 24);
    const nameLength = u16(view, at + 28);
    const extraLength = u16(view, at + 30);
    const commentLength = u16(view, at + 32);
    let localOffset = u32(view, at + 42);
    const rawName = decodeEntryName(bytes.subarray(at + 46, at + 46 + nameLength), flags);

    // ZIP64 extra field (0x0001) overrides the saturated 32-bit values, in a fixed order.
    if (
      compressedSize === 0xffffffff ||
      uncompressedSize === 0xffffffff ||
      localOffset === 0xffffffff
    ) {
      let e = at + 46 + nameLength;
      const extraEnd = e + extraLength;
      while (e + 4 <= extraEnd) {
        const id = u16(view, e);
        const size = u16(view, e + 2);
        if (id === 0x0001) {
          let p = e + 4;
          if (uncompressedSize === 0xffffffff) {
            uncompressedSize = Number(view.getBigUint64(p, true));
            p += 8;
          }
          if (compressedSize === 0xffffffff) {
            compressedSize = Number(view.getBigUint64(p, true));
            p += 8;
          }
          if (localOffset === 0xffffffff) localOffset = Number(view.getBigUint64(p, true));
          break;
        }
        e += 4 + size;
      }
    }

    out.push({
      rawName,
      method,
      compressedSize,
      uncompressedSize,
      localOffset,
      crc,
      flags,
      isDirectory:
        rawName.endsWith('/') ||
        rawName.endsWith('\\') ||
        (uncompressedSize === 0 && /[/\\]$/.test(rawName)),
    });
    at += 46 + nameLength + extraLength + commentLength;
  }
  return out;
}

/** Bit 11 of the general-purpose flags means the name is UTF-8; otherwise it is CP437.
 *  We decode the non-UTF-8 case as windows-1252, which agrees with CP437 on the ASCII range
 *  every real entry name lives in and is a label TextDecoder actually has. */
function decodeEntryName(bytes: Uint8Array, flags: number): string {
  const label = (flags & 0x0800) !== 0 ? 'utf-8' : 'windows-1252';
  try {
    return new TextDecoder(label).decode(bytes);
  } catch {
    return new TextDecoder('utf-8').decode(bytes);
  }
}

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw');
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(ds);
  const chunks: Uint8Array[] = [];
  let total = 0;
  const reader = stream.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value as Uint8Array);
    total += (value as Uint8Array).length;
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

async function deflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream('deflate-raw');
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(cs);
  const chunks: Uint8Array[] = [];
  let total = 0;
  const reader = stream.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value as Uint8Array);
    total += (value as Uint8Array).length;
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

export interface ZipReadOptions {
  /** Called between entries so a batch can cancel at a unit boundary. */
  readonly signal?: { readonly cancelled: boolean };
  readonly onProgress?: (done: number, total: number) => void;
}

/**
 * Expands an archive to one input per entry. NEVER throws for a bad ENTRY — only for an
 * archive with no readable central directory at all.
 */
export async function readZip(
  bytes: Uint8Array,
  opts: ZipReadOptions = {},
): Promise<ZipReadResult> {
  const central = readCentralDirectory(bytes);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const entries: ZipEntry[] = [];
  const skipped: ZipSkip[] = [];
  const taken = new Set<string>();

  for (let i = 0; i < central.length; i++) {
    if (opts.signal?.cancelled === true) break;
    opts.onProgress?.(i, central.length);
    const e = central[i] as CentralEntry;

    if (e.isDirectory) {
      skipped.push({
        rawName: e.rawName,
        reason: 'directory',
        detail: 'directory entries carry no content',
      });
      continue;
    }
    if ((e.flags & 0x0001) !== 0) {
      skipped.push({ rawName: e.rawName, reason: 'encrypted', detail: 'the entry is encrypted' });
      continue;
    }
    if (e.method !== METHOD_STORE && e.method !== METHOD_DEFLATE) {
      skipped.push({
        rawName: e.rawName,
        reason: 'unsupported-compression',
        detail: `compression method ${e.method} (only store and deflate are supported)`,
      });
      continue;
    }
    const safe = sanitizeZipEntryName(e.rawName);
    if (safe === null) {
      skipped.push({
        rawName: e.rawName,
        reason: 'unsafe-name',
        detail: 'absolute path, traversal segment, or a name that sanitises to nothing',
      });
      continue;
    }

    let data: Uint8Array;
    try {
      if (e.localOffset + 30 > bytes.length || u32(view, e.localOffset) !== LOCAL_HEADER_SIG) {
        throw new RangeError('local header missing');
      }
      const nameLength = u16(view, e.localOffset + 26);
      const extraLength = u16(view, e.localOffset + 28);
      const start = e.localOffset + 30 + nameLength + extraLength;
      const end = start + e.compressedSize;
      if (end > bytes.length) throw new RangeError('entry data runs past the end of the archive');
      const raw = bytes.subarray(start, end);
      data = e.method === METHOD_STORE ? raw.slice() : await inflateRaw(raw);
    } catch (err) {
      skipped.push({
        rawName: e.rawName,
        reason: 'corrupt',
        detail: err instanceof Error ? err.message : String(err),
      });
      continue;
    }

    if (data.length === 0) {
      skipped.push({ rawName: e.rawName, reason: 'empty', detail: 'the entry is zero bytes' });
      continue;
    }
    // The CRC is free integrity checking, and a mismatch means the bytes ARE wrong — better
    // one named skip than a parse error the user reads as "your file is broken".
    if (e.crc !== 0 && crc32(data) !== e.crc) {
      skipped.push({
        rawName: e.rawName,
        reason: 'crc-mismatch',
        detail: 'the entry failed its CRC check',
      });
      continue;
    }

    entries.push({ name: uniqueName(safe, taken), rawName: e.rawName, bytes: data });
  }
  opts.onProgress?.(central.length, central.length);
  return { entries, skipped };
}

/*───────────────────────────────────────────────────────────────────────────────────────
  One entry, by name — a Word document's body (SEO_BRIEF_06 §14)
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * The archive's entry names, as the central directory has them. Synchronous and cheap — no entry
 * is inflated — so a dropped file can be recognised as a Word document (`word/document.xml`)
 * from its bytes before anything is read. Empty for bytes that are not a ZIP at all.
 */
export function zipEntryNames(bytes: Uint8Array): readonly string[] {
  if (!isZip(bytes)) return [];
  try {
    return readCentralDirectory(bytes).map((e) => e.rawName);
  } catch {
    return [];
  }
}

/**
 * One entry's bytes, inflated and CRC-checked, or null when the archive has no such entry or the
 * entry cannot be read (encrypted, an unknown compression, corrupt). Only that entry is inflated:
 * a Word document's pictures are never decompressed to read its text.
 *
 * Throws only for bytes with no readable central directory, as `readZip` does.
 */
export async function readZipEntry(bytes: Uint8Array, name: string): Promise<Uint8Array | null> {
  const wanted = name.replace(/\\/g, '/').toLowerCase();
  const entry = readCentralDirectory(bytes).find(
    (e) => !e.isDirectory && e.rawName.replace(/\\/g, '/').toLowerCase() === wanted,
  );
  if (entry === undefined) return null;
  if ((entry.flags & 0x0001) !== 0) return null;
  if (entry.method !== METHOD_STORE && entry.method !== METHOD_DEFLATE) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  try {
    if (
      entry.localOffset + 30 > bytes.length ||
      u32(view, entry.localOffset) !== LOCAL_HEADER_SIG
    ) {
      return null;
    }
    const start =
      entry.localOffset +
      30 +
      u16(view, entry.localOffset + 26) +
      u16(view, entry.localOffset + 28);
    const end = start + entry.compressedSize;
    if (end > bytes.length) return null;
    const raw = bytes.subarray(start, end);
    const data = entry.method === METHOD_STORE ? raw.slice() : await inflateRaw(raw);
    if (entry.crc !== 0 && crc32(data) !== entry.crc) return null;
    return data;
  } catch {
    return null;
  }
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Writing — incremental
───────────────────────────────────────────────────────────────────────────────────────*/

interface PendingCentral {
  readonly record: ZipRecord;
  readonly offset: number;
}

/**
 * Builds an archive one entry at a time.
 *
 * `add()` emits the entry's bytes and keeps only its 46-byte central-directory record, so the
 * peak is one entry plus the directory rather than the whole archive. A caller that streams
 * the chunks straight out never holds the archive at all; `finish()` is the convenience for
 * one that does.
 */
export class ZipWriter {
  readonly #chunks: Uint8Array[] = [];
  readonly #central: PendingCentral[] = [];
  readonly #taken = new Set<string>();
  #offset = 0;
  #onChunk: ((chunk: Uint8Array) => void) | null;

  constructor(onChunk?: (chunk: Uint8Array) => void) {
    this.#onChunk = onChunk ?? null;
  }

  #emit(chunk: Uint8Array): void {
    this.#offset += chunk.length;
    if (this.#onChunk !== null) this.#onChunk(chunk);
    else this.#chunks.push(chunk);
  }

  /** Adds one entry. Collisions get ` (2)`, ` (3)`. Returns the name actually used. */
  async add(desiredName: string, data: Uint8Array, compress = true): Promise<string> {
    const safe = sanitizeZipEntryName(desiredName) ?? 'entry';
    const name = uniqueName(safe, this.#taken);
    const nameBytes = new TextEncoder().encode(name);
    const crc = crc32(data);

    let method = METHOD_STORE;
    let payload = data;
    if (compress && data.length > 64) {
      const deflated = await deflateRaw(data);
      if (deflated.length < data.length) {
        method = METHOD_DEFLATE;
        payload = deflated;
      }
    }

    const offset = this.#offset;
    // The fixed 1980-01-01 date is in the record builder, so output is REPRODUCIBLE.
    const record: ZipRecord = {
      name: nameBytes,
      crc,
      compressedSize: payload.length,
      uncompressedSize: data.length,
      method,
    };
    this.#emit(localHeader(record));
    this.#emit(payload);
    this.#central.push({ record, offset });
    return name;
  }

  /** Emits the central directory and the EOCD. Returns the whole archive when no `onChunk`
   *  was supplied, and an empty view when the caller streamed it. */
  finish(): Uint8Array {
    const cdStart = this.#offset;
    for (const e of this.#central) this.#emit(centralRecord(e.record, e.offset));
    this.#emit(endOfCentral(this.#central.length, this.#offset - cdStart, cdStart));

    if (this.#onChunk !== null) return new Uint8Array(0);
    const total = this.#chunks.reduce((n, c) => n + c.length, 0);
    const out = new Uint8Array(total);
    let at = 0;
    for (const c of this.#chunks) {
      out.set(c, at);
      at += c.length;
    }
    return out;
  }

  get entryCount(): number {
    return this.#central.length;
  }
}

export { DATA_DESCRIPTOR_SIG };
